import { Check, ChevronLeft, ExternalLink, Loader2, Plus, Search, X } from 'lucide-react';
import React, { useState } from 'react';

import { TaskStatusBadge } from '@/components/tasks/TaskStatusBadge';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { Task, TaskStatus } from '@/services/types/client-intake-types';
import { CanonicalTaskStatus } from '@/services/types/task-types';

import { RightPanelType } from './MatterRightActionBar';

interface PanelConfig {
  title: string;
  showAddButton?: boolean;
  addButtonLabel?: string;
  onAdd?: () => void;
}

const panelConfigs: Record<Exclude<RightPanelType, null>, PanelConfig> = {
  tasks: {
    title: 'Tasks',
    showAddButton: true,
    addButtonLabel: 'Add Task',
  },
  'notes-activity': {
    title: 'Notes & Activity',
    showAddButton: false,
  },
};

interface TaskDetailHeaderProps {
  task: Task;
  onStatusChange: (status: TaskStatus) => void;
  onComplete: () => void | Promise<void>;
}

interface MatterRightPanelProps {
  panelType: Exclude<RightPanelType, null>;
  onClose: () => void;
  onAdd?: () => void;
  onBack?: () => void;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  /** Props for task detail header mode (status dropdown + complete button) */
  taskDetailHeaderProps?: TaskDetailHeaderProps;
  /** Search props for inline expandable search in the header */
  searchProps?: {
    value: string;
    onChange: (value: string) => void;
  };
  /** URL to navigate to when clicking the "open in tasks page" icon */
  openInTasksPageUrl?: string;
}

export const MatterRightPanel: React.FC<MatterRightPanelProps> = ({
  panelType,
  onClose,
  onAdd,
  onBack,
  children,
  className,
  style,
  taskDetailHeaderProps,
  searchProps,
  openInTasksPageUrl,
}) => {
  const config = panelConfigs[panelType];
  const isTaskDetailView = Boolean(onBack && taskDetailHeaderProps);
  const [isCompleting, setIsCompleting] = useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const searchContainerRef = React.useRef<HTMLDivElement>(null);

  const expandSearch = React.useCallback(() => {
    setIsSearchExpanded(true);
    const el = searchContainerRef.current;
    if (el) {
      const onEnd = () => {
        el.removeEventListener('transitionend', onEnd);
        searchInputRef.current?.focus();
      };
      el.addEventListener('transitionend', onEnd);
    }
  }, []);

  const handleComplete = async () => {
    setIsCompleting(true);
    try {
      await taskDetailHeaderProps?.onComplete();
    } finally {
      setIsCompleting(false);
    }
  };

  return (
    <div
      className={cn(
        'flex flex-col bg-white border border-zinc-200 rounded-xl overflow-hidden',
        className,
      )}
      style={style}
    >
      {/* Panel Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-100">
        {isTaskDetailView && taskDetailHeaderProps ? (
          /* Task detail view: back button + status dropdown */
          <div className="flex items-center gap-1.5">
            <Button
              variant="ghost"
              onClick={onBack}
              className="h-5 w-5 p-0"
              size="sm"
            >
              <ChevronLeft className="h-3 w-3" />
            </Button>
            <TaskStatusBadge
              status={taskDetailHeaderProps.task.status}
              onStatusChange={taskDetailHeaderProps.onStatusChange}
              task={taskDetailHeaderProps.task}
            />
          </div>
        ) : onBack ? (
          <Button
            variant="ghost"
            onClick={onBack}
            className="h-auto text-xs font-medium p-1 gap-0.5"
            size="sm"
          >
            <ChevronLeft className="h-3 w-3" />
            <span className="text-[10px]">All Tasks</span>
          </Button>
        ) : (
          <div className="flex items-center gap-1">
            <h3 className="text-xs font-bold text-zinc-800 uppercase tracking-wide">
              {config.title}
            </h3>
            {openInTasksPageUrl && !onBack && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <a
                    href={openInTasksPageUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center h-4 w-4 rounded text-zinc-400 hover:text-zinc-600 transition-colors focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:outline-none"
                    aria-label="Open in Tasks page"
                  >
                    <ExternalLink strokeWidth={2.5} className="h-3 w-3" />
                  </a>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs">
                  Open in Tasks page
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        )}
        <div className="flex items-center gap-1.5">
          {isTaskDetailView && taskDetailHeaderProps ? (
            /* Task detail view: Complete button */
            taskDetailHeaderProps.task.status.canonical_status !== CanonicalTaskStatus.COMPLETED && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleComplete}
                disabled={isCompleting}
                className="h-6 text-[10px] px-2 border-green-200 bg-green-50 text-green-700 hover:bg-green-100 hover:text-green-800"
              >
                {isCompleting ? (
                  <Loader2 className="h-3 w-3 mr-0.5 animate-spin" />
                ) : (
                  <Check className="h-3 w-3 mr-0.5" />
                )}
                Complete
              </Button>
            )
          ) : (
            /* List view: Search icon + Open in Tasks Page + Add Task button (only when not in task detail) */
            <>
              {searchProps && !onBack && (
                <div
                  ref={searchContainerRef}
                  className={cn(
                    'inline-flex items-center h-7 rounded-full bg-zinc-100 overflow-hidden transition-[width,padding] duration-250 ease-[cubic-bezier(0.25,0.1,0.25,1)]',
                    isSearchExpanded ? 'w-44 gap-1.5 px-2.5' : 'w-7 cursor-pointer hover:bg-zinc-200',
                  )}
                  onClick={() => {
                    if (!isSearchExpanded) expandSearch();
                  }}
                  role={isSearchExpanded ? undefined : 'button'}
                  tabIndex={isSearchExpanded ? undefined : 0}
                  aria-label={isSearchExpanded ? undefined : 'Search tasks'}
                  onKeyDown={(e) => {
                    if (!isSearchExpanded && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      expandSearch();
                    }
                  }}
                >
                  {!isSearchExpanded && (
                    <Search strokeWidth={2.5} className="h-3.5 w-3.5 text-zinc-600 mx-auto" />
                  )}
                  {isSearchExpanded && (
                    <>
                      <Search strokeWidth={2.5} className="h-3.5 w-3.5 text-zinc-500 shrink-0" />
                      <input
                        ref={searchInputRef}
                        type="text"
                        role="searchbox"
                        aria-label="Search tasks"
                        autoComplete="off"
                        placeholder="Search..."
                        value={searchProps.value}
                        onChange={(e) => searchProps.onChange(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') {
                            searchProps.onChange('');
                            setIsSearchExpanded(false);
                          }
                        }}
                        onBlur={() => {
                          if (!searchProps.value) {
                            setIsSearchExpanded(false);
                          }
                        }}
                        className="flex-1 min-w-0 bg-transparent text-[11px] font-semibold text-zinc-900 placeholder:text-zinc-500 placeholder:font-semibold focus:outline-none"
                      />
                      {searchProps.value && (
                        <button
                          type="button"
                          aria-label="Clear search"
                          onClick={() => {
                            searchProps.onChange('');
                            searchInputRef.current?.focus();
                          }}
                          className="shrink-0"
                        >
                          <X strokeWidth={2.5} className="h-3 w-3 text-zinc-400 hover:text-zinc-600" />
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}
              {config.showAddButton && !onBack && (
                <button
                  type="button"
                  onClick={onAdd}
                  className="flex items-center h-7 rounded-full bg-zinc-900 text-white px-3 gap-1 text-[11px] font-semibold hover:bg-zinc-800 active:scale-95 transition-all duration-150"
                >
                  <Plus className="h-3 w-3" />
                  {config.addButtonLabel}
                </button>
              )}
            </>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-6 w-6 p-0"
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      </div>

      {/* Panel Content */}
      <div className="flex-1 overflow-y-auto scrollbar-thin">{children}</div>
    </div>
  );
};

export default MatterRightPanel;
